/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, {
	type ReactNode,
	useMemo,
	useEffect,
	useRef,
	useCallback,
	StrictMode,
	memo,
} from 'react';
import { addBreadcrumb } from '@sentry/browser';
import { createPath } from 'history/PathUtils';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import getUFORouteName from '@atlaskit/react-ufo/route-name';
import traceUFORedirect from '@atlaskit/react-ufo/trace-redirect';
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { PlaceholderWithNativeSuspense } from '@atlassian/jira-placeholder/src/index.tsx';
import {
	useAnalyticsEvents,
	fireOperationalAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { stopInitialPageLoadTimingFromPerformanceMarkStart } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { useSpaStateActions } from '@atlassian/jira-spa-state-controller/src/common/index.tsx';
import { createCheckShouldResetPage } from '@atlassian/jira-spa-state-controller/src/main.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import {
	type Location,
	type MatchedRoute,
	matchRoute,
	createRouterSelector,
	useRouterActions,
	type Route,
	type Match,
	type HistoryAction,
	type Query,
} from '@atlassian/react-resource-router';
import { useShouldPerformAction } from '@atlassian/remote-actions/controllers/use-should-perform-action';
import type { RemoteAction } from '@atlassian/remote-actions/types';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import sendExperienceAnalytics from '@jira/platform__experience-tracking-analytics/src/index.tsx';
import {
	TASK_ABORT,
	TASK_FAIL,
} from '@jira/platform__jira-experience-tracker/src/common/constants.tsx';
import { truncateStackTrace } from '@jira/issue__issue-view-errors/src/truncateStackTrace.tsx';
import {
	isClientFetchError,
	isHttpClientErrorResponse,
	isWebCrawlerUserAgentWithAppleBot,
} from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { isTabTooOld } from '../services/stale-version-reload/isTabTooOld.tsx';
import transitionValidator from '../services/transition-validator/index.tsx';
import { RouteEntryPointContainer } from './entry-point-container/index.tsx';

type Props = {
	initRoute: MatchedRoute | null;
	routes: Route[];
};
const REMOTE_ACTIONS_OFF = 'OFF' as const;
const REMOTE_ACTIONS_PERFORM = 'PERFORM' as const;
const useRouterLocation = createRouterSelector((state) => state.location);
const useRouterAction = createRouterSelector((state) => state.action);
const useRouterQuery = createRouterSelector((state) => state.query);
const useRouterRoute = createRouterSelector((state) => state.route);
const useRouterMatch = createRouterSelector((state) => state.match);
const useRouterRoutes = createRouterSelector((state) => state.routes);
const useIsLazyRoutes = createRouterSelector((state) => state.isLazyRoutes);
let hasPendingForceReloadAction = false;

const getRemoteActionsRolloutStage = () =>
	fg('remote_actions_-_killswitch_-_temporary') ? REMOTE_ACTIONS_PERFORM : REMOTE_ACTIONS_OFF;

// @ExportForTesting
export const setHasPendingForceReloadAction = (value: boolean): void => {
	hasPendingForceReloadAction = value;
};
// @ExportForTesting
export const getHasPendingForceReloadAction = (): boolean => hasPendingForceReloadAction;
const fireRemoteActionAnalytics = (
	analyticsEvent: UIAnalyticsEvent,
	eventAction: string,
	remoteAction: RemoteAction,
	currentRoute: MatchedRoute | null,
	targetRoute: MatchedRoute | null,
	attempts: number,
	lastAttemptedAt: number | undefined,
) =>
	fireOperationalAnalytics(analyticsEvent, `remoteAction ${eventAction}`, {
		source: 'transitionBlocker',
		actionId: remoteAction != null && remoteAction.id != null ? String(remoteAction.id) : '',
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		frontendVersion: window.BUILD_KEY,
		currentRouteName: currentRoute?.route.name || '',
		targetRouteName: targetRoute?.route.name || '',
		remoteActionsRolloutStage: getRemoteActionsRolloutStage(),
		attempts,
		lastAttemptedAt,
	});
const fireStaleTabReloadAnalytics = (
	analyticsEvent: UIAnalyticsEvent,
	currentRoute: MatchedRoute | null,
	targetRoute: MatchedRoute | null,
) =>
	fireOperationalAnalytics(analyticsEvent, 'staleTabReload performed', {
		currentRouteName: currentRoute?.route.name ?? 'unknown',
		targetRouteName: targetRoute?.route.name ?? 'unknown',
	});
const updateSentryBreadcrumb = (route: Route, nextRoute: Route | undefined) => {
	const from = route.path + (route.query ? `?${route.query.join('&')}` : '');
	const to = nextRoute?.path + (nextRoute?.query ? `?${nextRoute.query.join('&')}` : '');
	addBreadcrumb({
		type: 'navigation',
		data: {
			from,
			to,
		},
	});
};

export const appendIgnoreStickyVersion = (search?: string): string => {
	const searchParams = new URLSearchParams(search);
	if (!searchParams.has('ignoreStickyVersion')) {
		searchParams.append('ignoreStickyVersion', 'true');
	}
	return `?${searchParams}`;
};

// New SPA

type RouterProps = {
	location: Location;
	route: Route;
	match: Match;
	action: HistoryAction;
	query: Query;
};

const RegisterRouterBlocks = ({
	routes: routesOriginal,
	initRoute,
	route,
	match,
}: Props & {
	route: Route;
	match: Match;
}) => {
	const isLazyRoutes = useIsLazyRoutes();
	const routerRoutes = useRouterRoutes();
	const routes = useMemo(() => {
		return isLazyRoutes ? routerRoutes : routesOriginal;
	}, [isLazyRoutes, routerRoutes, routesOriginal]);

	const checkShouldResetPage = useMemo(() => createCheckShouldResetPage(), []);
	const currentMatchObjectRef = useRef(initRoute);

	const { registerBlock } = useRouterActions();

	const { shouldPerformAction, performAction } = useShouldPerformAction({
		actionName: 'forceReload',
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		/* We update currentMatch only if it's not a redirect. So in canTransitionIn we can compare
		 * actual routes instead of comparing against the redirect one */
		if (route.isRedirect !== true) {
			currentMatchObjectRef.current = {
				route,
				match,
			};
		}
	}, [match, route]);
	const { resetCurrentPage } = useSpaStateActions();

	/* We regiser the blocker on every render to be sure that it gets added as first item
	 * even when the route changes (so it gets called last). It will be added only once anyway.
	 * This is our custom block() API that accepts promises.
	 * This code allows us to disable an SPA transition and trigger a full page reload
	 * in case the route we are moving out (or in) needs it. */
	const transitionBlocker = useCallback(
		(nextLocation: Location, nextAction: 'PUSH' | 'REPLACE') => {
			const nextMatch = matchRoute(routes, nextLocation.pathname, nextLocation.search);
			const canDo = transitionValidator(currentMatchObjectRef.current, nextMatch);

			// Checking how many routes is a proxy for determining whether the lazy routes have been loaded
			// canDo might still be true if this is a transition from an issue to an issue
			if (isLazyRoutes && !canDo && routes.length <= 3) {
				log.safeInfoWithoutCustomerData('lazy_routes.transition', 'failure', {
					route: nextMatch?.route.name,
				});
			}

			if (!canDo) {
				const windowAction = nextAction === 'REPLACE' ? 'replace' : 'assign';

				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location[windowAction](createPath(nextLocation));
			}
			const remoteActionsRolloutStage = getRemoteActionsRolloutStage();
			if (remoteActionsRolloutStage !== REMOTE_ACTIONS_OFF && createAnalyticsEvent !== null) {
				const currentMatch = currentMatchObjectRef.current;
				if (
					canDo &&
					currentMatch?.route.name !== nextMatch?.route.name &&
					shouldPerformAction &&
					hasPendingForceReloadAction === false
				) {
					hasPendingForceReloadAction = true;
					const windowAction = nextAction === 'REPLACE' ? 'replace' : 'assign';
					performAction(
						(remoteAction, { attempts, lastAttemptedAt }) => {
							fireRemoteActionAnalytics(
								createAnalyticsEvent({}),
								'performed',
								remoteAction,
								currentMatch,
								nextMatch,
								attempts,
								lastAttemptedAt,
							);

							// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
							window.location[windowAction](
								createPath({
									...nextLocation,
									search: appendIgnoreStickyVersion(nextLocation.search),
								}),
							);
						},
						(remoteAction, { attempts, lastAttemptedAt }) => {
							fireRemoteActionAnalytics(
								createAnalyticsEvent({}),
								'cancelled',
								remoteAction,
								currentMatch,
								nextMatch,
								attempts,
								lastAttemptedAt,
							);
						},
					);
				}
			}

			// Force a full reload on navigation for tabs older than 7 days. Reuses the remote-actions
			// pending-reload guard above so the two paths can't both fire for one transition.
			if (fg('jira_stale_tab_reload_on_navigation')) {
				const currentMatch = currentMatchObjectRef.current;
				if (
					canDo &&
					currentMatch?.route.name !== nextMatch?.route.name &&
					hasPendingForceReloadAction === false &&
					isTabTooOld()
				) {
					hasPendingForceReloadAction = true;
					if (createAnalyticsEvent !== null) {
						fireStaleTabReloadAnalytics(createAnalyticsEvent({}), currentMatch, nextMatch);
					}
					const windowAction = nextAction === 'REPLACE' ? 'replace' : 'assign';
					// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
					window.location[windowAction](
						createPath({
							...nextLocation,
							search: appendIgnoreStickyVersion(nextLocation.search),
						}),
					);
					// Block the SPA transition - the full page reload above replaces it.
					return false;
				}
			}
			return canDo;
		},
		[performAction, routes, shouldPerformAction, createAnalyticsEvent, isLazyRoutes],
	);
	const resetCurrentPageBeforeRouteChangeBlocker = useCallback(
		(nextLocation: Location, nextAction: 'PUSH' | 'REPLACE' | 'POP') => {
			const nextMatchObject = matchRoute(routes, nextLocation.pathname, nextLocation.search);
			if (nextMatchObject && match && route) {
				const { match: nextMatch, route: nextRoute } = nextMatchObject;
				const shouldReset = checkShouldResetPage({
					nextMatch,
					nextRoute,
					currentMatch: match,
					currentRoute: route,
					action: nextAction,
				});
				const nextRouteUfoName = getUFORouteName(nextRoute);
				if (shouldReset) {
					resetCurrentPage(nextRouteUfoName, nextRoute.name);
					GlobalPageLoadExperience.setPageLoadId(nextRoute.perfMetricKey || 'UNKNOWN');
				}
				const isCurrentRouteRedirecting = route.isRedirect === true;
				if (isCurrentRouteRedirecting) {
					const currentRouteName = getUFORouteName(route);
					traceUFORedirect(currentRouteName, nextRouteUfoName, nextRoute.name, performance.now());
				}
			}
			updateSentryBreadcrumb(route, nextMatchObject?.route);
			return true;
		},
		[checkShouldResetPage, match, resetCurrentPage, route, routes],
	);
	registerBlock(transitionBlocker);
	registerBlock(resetCurrentPageBeforeRouteChangeBlocker);

	return null;
};

const SpaRouteComponent = memo(({ location, action, query, route, match }: RouterProps) => {
	const { component: RouteComponent, isStrictModeEnabled, skeleton } = route;
	const Skeleton = skeleton || Spinner;

	if (RouteComponent) {
		return (
			<ConditionalStrictMode isEnabled={isStrictModeEnabled}>
				<PlaceholderWithNativeSuspense name="route-component" fallback={<Skeleton />}>
					<RouteComponent
						location={location}
						route={route}
						match={match}
						action={action}
						query={query}
					/>
				</PlaceholderWithNativeSuspense>
			</ConditionalStrictMode>
		);
	}
});

const SpaRouteEntryPointContainer = memo(
	({ location, action, query, route, match }: RouterProps) => {
		return (
			<RouteEntryPointContainer
				location={location}
				route={route}
				match={match}
				action={action}
				query={query}
			/>
		);
	},
);

const sendRouteExperienceFailEvent = (
	experienceName: string,
	location: string,
	error?: Error,
): void => {
	if (isWebCrawlerUserAgentWithAppleBot()) {
		return;
	}

	if (error && isClientFetchError(error)) {
		return;
	}

	const isAbortable = Boolean(error && isHttpClientErrorResponse(error));

	const errorStack = truncateStackTrace(error?.stack);
	const userAgent = globalThis.navigator?.userAgent ?? 'unknown';
	const isWebDriver = globalThis.navigator?.webdriver;

	sendExperienceAnalytics({
		experience: experienceName,
		analyticsSource: `${experienceName}Route`,
		action: isAbortable ? TASK_ABORT : TASK_FAIL,
		wasExperienceSuccesful: false,
		application: null,
		edition: null,
		additionalAttributes: {
			errorMessage: error?.message ?? 'Unknown error',
			location,
			...(errorStack !== undefined && { errorStack }),
			userAgent,
			...(isWebDriver !== undefined && { isWebDriver }),
		},
	});
};

const SpaChild = ({ location, action, query, route, match }: RouterProps) => {
	const reporting = route.meta?.reporting;
	const experienceName = reporting?.experienceName;

	let onError = reporting?.onError;
	if (experienceName != null && fg('jira_route_error_boundary_experience_event')) {
		onError = (errorLocation: string, error: Error) =>
			sendRouteExperienceFailEvent(experienceName, errorLocation, error);
	}

	const {
		experienceName: _experienceName,
		onError: _onError,
		...errorBoundaryReporting
	} = reporting ?? {};

	const entryPoint = useMemo(() => route.entryPoint?.(), [route]);

	/* Render a <Route> component instead of the page directly so the match prop can be
	 * propagated down to components wrapped with the withRouter HOC. Unfortunately, this
	 * means we forfeit the right to access query params via match is provided by <Route>
	 * rather than our custom matchRoute function. */
	return (
		<UFOSegment name="route-component">
			<JSErrorBoundary
				// explicitly coerce undefined to string to make flow happy
				id={`routeApp.${String(route.name)}`}
				packageName="jiraSpa"
				extraEventData={useMemo(
					() => ({
						routeGroup: route.group ?? '',
						isSSR: __SERVER__,
					}),
					[route.group],
				)}
				fallback="page"
				sendToPrivacyUnsafeSplunk={Boolean(reporting)}
				{...errorBoundaryReporting}
				onError={onError}
			>
				{entryPoint ? (
					<SpaRouteEntryPointContainer
						location={location}
						action={action}
						query={query}
						route={route}
						match={match}
					/>
				) : (
					<SpaRouteComponent
						location={location}
						action={action}
						query={query}
						route={route}
						match={match}
					/>
				)}
			</JSErrorBoundary>
		</UFOSegment>
	);
};

/**
 * Manages the routing, analytics, and remote actions within a single-page application (SPA).
 * This component sets up initial routes, enables analytics tracking, and handles SPA transitions.
 * It incorporates custom logic for page reloading or redirection under certain conditions
 * and ensures safe rendering of route components within an error boundary context.
 */
const Spa = ({ routes, initRoute = null }: Props): React.JSX.Element => {
	useMemo(() => {
		setMark('jira-spa/view.start');
	}, []);
	useEffect(() => {
		stopInitialPageLoadTimingFromPerformanceMarkStart('jira-spa/view', 'jira-spa/view.start', true);
	}, []);
	const location = useRouterLocation();
	const action = useRouterAction();
	const query = useRouterQuery();
	const route = useRouterRoute();
	const match = useRouterMatch();

	return (
		<>
			<RegisterRouterBlocks routes={routes} initRoute={initRoute} route={route} match={match} />
			{!(route && match) ? null : (
				<SpaChild location={location} action={action} query={query} route={route} match={match} />
			)}
		</>
	);
};

type ConditionalStrictModeProps = {
	isEnabled?: boolean;
	children: ReactNode;
};

function ConditionalStrictMode(props: ConditionalStrictModeProps): JSX.Element {
	// Default to true
	const { isEnabled = true, children } = props;

	// This flag just gives us a kill switch
	if (isEnabled && fg('jfp-jira-incremental-strict-mode')) {
		return <StrictMode>{children}</StrictMode>;
	}

	return <>{children}</>;
}

export default Spa;
